import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { backendUrl } from "../../api/constants";
import Button from "../Button/Button";
import { useSnackbar } from "notistack";
import Notice from "../ui/notice";
import { useUser } from "../../context/UserContext/UserProvider";
import VenueCard from "../VenueCard/VenueCard";
import EditVenue from "../ui/EditVenue";
import { modal } from '../../util/useModal';
import Input from "../Input/Input";
import { parseGoogleAddress } from "lib";
import { FcGoogle } from "react-icons/fc";
import { IoIosArrowBack } from "react-icons/io";
import Logo from "../../assets/imgs/inyc.svg";
import AppStatusScreen from "../AppStatusScreen/AppStatusScreen";
import KitButton from "../kit/Button";

const VENUE_SUBMIT_SUCCESS = {
  title: "venue submitted",
  subtitle: (
    <>
      we'll review and email you with confirmation or questions
      <div
        className="mt-kit-12 font-['Primary-Bold'] text-kit-base leading-[1.25] tracking-[-0.02em]"
        style={{
          fontFamily: "Primary-Bold",
          fontWeight: 700,
          color: "var(--inyc-primary-text-color)",
        }}
      >
        thanks for contributing!
      </div>
      <div
        className="mt-kit-12 font-['Primary-Regular'] text-kit-base font-normal leading-[1.25] tracking-[-0.02em]"
        style={{
          fontFamily: "Primary-Regular",
          fontWeight: 400,
          color: "var(--inyc-primary-text-color-light)",
        }}
      >
        if you own/manage the submitted venue, once it's approved, you'll be able to "claim" it and connect to your account
      </div>
    </>
  ),
};

const VenueStatusScreen = ({ title, subtitle, proceed }) => (
  <AppStatusScreen
    title={title}
    subtitle={subtitle}
    actions={
      <KitButton variant="primary" className="min-w-[7.4rem]" onClick={() => proceed(true)}>
        done
      </KitButton>
    }
  />
);

const showVenueStatusModal = (title, subtitle) =>
  modal(
    ({ proceed }) => (
      <VenueStatusScreen title={title} subtitle={subtitle} proceed={proceed} />
    ),
    { fullScreen: true, raw: true }
  );

const VENUE_FLOW = {
  SEARCH: "search",
  EXISTING_SELECTED: "existing_selected",
  GOOGLE_SEARCH: "google_search",
  GOOGLE_SELECTED: "google_selected",
  MANUAL_CREATE: "manual_create",
};

const MyVenuesPanel = ({
  userLocation,
  refreshVenues,
  refreshAll,
  venues,
  selectedVenue = {},
  setSelectedVenue,
  handleVenuPick,
}) => {
  const { user } = useUser();
  const nav = useNavigate();
  const [venueItems, setVenueItems] = useState();
  const [venueLoading, setVenueLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [postError, setPostError] = useState();
  const [formResetKey, setFormResetKey] = useState(0);
  const [flow, setFlow] = useState(VENUE_FLOW.SEARCH);
  const [venueSearchQuery, setVenueSearchQuery] = useState("");
  const [googleSearchQuery, setGoogleSearchQuery] = useState("");
  const [googleResults, setGoogleResults] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const venuSearchTimer = useRef();
  const googleSearchTimer = useRef();
  const editVenueRef = useRef();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (selectedVenue?.marker) {
      refreshVenues(selectedVenue.marker);
    }
  }, [selectedVenue?.marker]);

  const onVenueChange = async (e) => {
    const query = e.target.value;
    setVenueSearchQuery(query);
    setPostError(null);

    if (query.trim().length < 2) {
      clearTimeout(venuSearchTimer.current);
      setVenueItems([]);
      setVenueLoading(false);
      return;
    }

    clearTimeout(venuSearchTimer.current);
    setVenueLoading(true);
    venuSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(
          `${backendUrl}/venues/search?q=${encodeURIComponent(query)}`
        );
        if (data.error) {
          console.log(data.error);
        } else {
          setVenueItems(data.venues || []);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setVenueLoading(false);
      }
    }, 500);
  };

  const resetVenueFlow = () => {
    setSelectedVenue({ venue: {} });
    setVenueItems([]);
    setVenueSearchQuery("");
    setGoogleSearchQuery("");
    setGoogleResults([]);
    setPostError(null);
    setFlow(VENUE_FLOW.SEARCH);
    setFormResetKey((key) => key + 1);
  };

  const handleExistingVenuePick = (venue) => {
    setPostError(null);
    handleVenuPick(venue);
    setFlow(VENUE_FLOW.EXISTING_SELECTED);
  };

  const handleStartGoogleSearch = () => {
    setPostError(null);
    const query = venueSearchQuery.trim();
    setFlow(VENUE_FLOW.GOOGLE_SEARCH);
    setGoogleSearchQuery(query);
    setSelectedVenue({
      venue: {
        name: query,
      },
    });

    if (query.length >= 2) {
      searchGoogleVenues(query);
    }
  };

  const handleStartManualCreate = () => {
    setPostError(null);
    setSelectedVenue({ venue: {} });
    setFlow(VENUE_FLOW.MANUAL_CREATE);
    setFormResetKey((key) => key + 1);
  };

  const searchGoogleVenues = async (query) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      setGoogleResults([]);
      setGoogleLoading(false);
      return;
    }

    setGoogleLoading(true);
    try {
      let url = `${backendUrl}/influencer/geolocation/autocomplete?${new URLSearchParams({
        input: trimmedQuery,
      })}`;

      if (userLocation?.lat && userLocation?.lng) {
        url += `&latitude=${userLocation.lat}&longitude=${userLocation.lng}&radius=50000`;
      }

      const { data } = await axiosInstance.get(url);
      setGoogleResults((data || []).filter((suggestion) => !suggestion._id));
    } catch (error) {
      console.log(error);
      setGoogleResults([]);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleSearchChange = (e) => {
    const query = e.target.value;
    setGoogleSearchQuery(query);
    setPostError(null);
    clearTimeout(googleSearchTimer.current);

    if (query.trim().length < 2) {
      setGoogleResults([]);
      setGoogleLoading(false);
      return;
    }

    setGoogleLoading(true);
    googleSearchTimer.current = setTimeout(() => {
      searchGoogleVenues(query);
    }, 500);
  };

  const handleGoogleResultSelect = async (suggestion) => {
    if (!suggestion?.place_id) return;

    setGoogleLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `${backendUrl}/influencer/geolocation/place?${new URLSearchParams({
          place_id: suggestion.place_id,
        })}`
      );

      if (!data) return;

      const googleAddress = parseGoogleAddress(data.address_components);
      const venueName =
        data.name ||
        suggestion.structured_formatting?.main_text ||
        suggestion.description ||
        "";
      const addressFields = {
        ...googleAddress,
        venueName,
      };

      setSelectedVenue({
        venue: {
          name: venueName,
          address: data.formatted_address || "",
          addressFields,
          location: {
            type: "Point",
            coordinates: [
              data.geometry.location.lng,
              data.geometry.location.lat,
            ],
          },
        },
      });
      setGoogleResults([]);
      setFlow(VENUE_FLOW.GOOGLE_SELECTED);
    } catch (error) {
      console.log(error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setSelectedVenue((prev) => ({
      ...prev,
      venue: {
        ...prev.venue,
        [e.target.name]: e.target.value,
      },
    }));
  };

  const postVenue = async () => {
    setLoading(true);

    const { form } = editVenueRef.current?.getFormData() || {
      form: new FormData(),
    };

    const mine = selectedVenue.venue?._id && selectedVenue.venue?.me;
    const endpoint = mine
      ? "update"
      : selectedVenue.venue?._id
        ? "claim"
        : "post";

    if (endpoint !== "post") {
      form.append(
        "venue_id",
        selectedVenue.venue?._id
          ? selectedVenue.venue._id.toString()
          : undefined
      );
    }

    try {
      const { data } = await axiosInstance.post(
        `${backendUrl}/business/${endpoint}`,
        form,
        {
          "Content-Type": "multipart/form-data",
        }
      );
      if (data.error) {
        setPostError(null);
        if (typeof data.error === 'string') {
          setPostError(data.error);
          if (data.ticket) {
            enqueueSnackbar(data.error, { variant: "warning" });
          }
        } else {
          for (const key in data.error) {
            if (data.error[key].hasOwnProperty("error")) {
              if (key === "address") {
                editVenueRef.current?.setAddressErrors?.(data.error[key].error || data.error[key]);
              }
              setPostError(data.error[key].error);
            }
          }
        }
      } else {
        setPostError(null);
        if (endpoint === "claim") {
          await showVenueStatusModal(
            "your claim request was submitted!",
            "we'll reach out soon to verify"
          );
        } else if (endpoint === "update") {
          await showVenueStatusModal(
            "your venue was updated successfully!",
            "our team will review your changes and you can check its status in your venues list."
          );
        } else {
          await showVenueStatusModal(
            VENUE_SUBMIT_SUCCESS.title,
            VENUE_SUBMIT_SUCCESS.subtitle
          );
        }
        refreshAll(selectedVenue?.marker);
        if (endpoint === "post") {
          setSelectedVenue({ venue: {} });
          setVenueItems([]);
          setVenueSearchQuery("");
          setFlow(VENUE_FLOW.SEARCH);
          setFormResetKey((key) => key + 1);
        }
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  // console.log("Selected Venue", selectedVenue);
  // console.log('user id', user?._id)

  const selectedExistingVenue = selectedVenue.venue?._id;
  const selectedVenueIsOwned =
    selectedVenue.venue?.me || selectedVenue.venue?.account_id === user?._id;
  const selectedVenueIsClaimed =
    selectedExistingVenue && selectedVenue.venue?.account_id && !selectedVenueIsOwned;
  const selectedVenueCanBeClaimed =
    selectedExistingVenue && !selectedVenue.venue?.account_id && !selectedVenueIsOwned;
  const shouldShowForm =
    flow === VENUE_FLOW.GOOGLE_SELECTED ||
    flow === VENUE_FLOW.MANUAL_CREATE ||
    (selectedExistingVenue && selectedVenueIsOwned);
  const shouldShowFallbackActions =
    flow === VENUE_FLOW.SEARCH && venueSearchQuery.trim().length >= 2;
  const shouldShowVenueNoResults =
    shouldShowFallbackActions && !venueLoading && venueItems?.length === 0;

  const getVenueResultStatus = (venue) => {
    if (venue.account_id === user?._id || venue.me) return "owned by you";
    if (venue.account_id) return "claimed";
    return "claimable";
  };

  const renderFlowBack = (className = "mb-kit-16") => (
    <div className={className}>
      <div
        className="arrow-events-container"
        role="button"
        tabIndex={0}
        aria-label="back"
        onClick={resetVenueFlow}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            resetVenueFlow();
          }
        }}
        style={{
          width: "45px",
          height: "45px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "0.3s",
        }}
      >
        <IoIosArrowBack className="arrow-icon" size={20} />
      </div>
    </div>
  );

  const renderFormTitle = () => {
    if (selectedExistingVenue && selectedVenueIsOwned) return "edit your venue";
    if (flow === VENUE_FLOW.GOOGLE_SELECTED) return "review and add to venue";
    return "create your venue manually";
  };

  const renderSubmitLabel = () => {
    if (selectedVenue.venue?.me) return "update venue";
    if (selectedExistingVenue) return "claim venue";
    return "post venue";
  };

  return (
    <div
      style={{
        background: "var(--inyc-primary)",
        padding: "var(--inyc-space-16)",
        paddingTop: "var(--inyc-space-24)",
      }}
    >
      {flow === VENUE_FLOW.EXISTING_SELECTED && selectedExistingVenue && (
        <div className="mx-auto max-w-[1000px] -mt-[14px] px-kit-16">
          {renderFlowBack()}
        </div>
      )}
      <div
        className={`md:!mx-auto max-w-[1000px] ${
          flow === VENUE_FLOW.EXISTING_SELECTED ? "" : "-mt-[14px]"
        }`}
        style={{ marginRight: "var(--inyc-space-20)", marginLeft: "var(--inyc-space-20)" }}
      >
        {flow === VENUE_FLOW.SEARCH && (
          <>
            <div className="mt-kit-16 mb-kit-20 px-kit-16 text-[var(--inyc-primary-text-color)]">
              <div className="text-kit-md font-['Primary-Medium']">post a venue</div>
              <div className="mt-kit-8 font-['Primary-Regular'] text-kit-sm text-[var(--inyc-primary-text-color-light)]">
                owner or representative of the venue you're posting? upon approval of your post, you'll be able to claim the venue as yours, which will link it to your account for you to edit at any time.
              </div>
            </div>
            <div className="px-kit-16">
              <div className="mb-kit-20 -mt-[10px] border-b-[1px] border-[color:color-mix(in_srgb,var(--inyc-outline-color)_35%,transparent)]" />
            </div>
            <Notice noicon className="mb-kit-10 !mt-0 !border-0 !py-kit-2 !pl-kit-16 !pr-0 !text-kit-base [&>div]:!text-kit-base [&>div]:font-['Primary-Regular'] [&>div]:!text-[var(--inyc-primary-text-color)]">
              first let's see if it's already in—yc verified:
            </Notice>
            <Input
              key={`venue-search-${formResetKey}`}
              className="!mt-0 mb-kit-24 [&_input.ui-input]:!text-kit-xs [&_input.ui-input::placeholder]:!text-kit-xs"
              inputStyle={{ fontSize: "var(--inyc-text-xs)" }}
              style={{ ["--inyc-input-placeholder-size"]: "0.8rem" }}
              placeHolder="search for your venue..."
              onChange={onVenueChange}
              value={venueSearchQuery}
            />
          </>
        )}

        {flow === VENUE_FLOW.SEARCH && (
          <div className="-mt-[6px] px-kit-16">
            <div className="mx-auto mt-kit-10 flex max-h-[22rem] max-w-[680px] flex-col gap-kit-10 overflow-y-auto px-kit-2 pb-kit-4">
              {venueLoading && (
                <div className="rounded-kit-md border border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16 text-center text-kit-sm font-['Primary-Regular'] text-[var(--inyc-primary-text-color-light)]">
                  searching in-yc venues...
                </div>
              )}
              {!venueLoading && venueItems?.map((venue) => (
                <button
                  key={venue._id}
                  type="button"
                  onClick={() => handleExistingVenuePick(venue)}
                  className="venue-search-result-card relative w-full overflow-visible rounded-kit-md border border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] px-kit-16 pb-kit-16 pt-kit-20 text-left transition-all duration-200 hover:border-[var(--inyc-secondary)] hover:bg-[var(--inyc-primary)]"
                >
                  <div className="flex translate-y-[5px] items-center justify-between gap-kit-12">
                    <div className="flex min-w-0 items-center gap-kit-8">
                      <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-['Primary-Medium'] text-kit-md text-[var(--inyc-primary-text-color)]">
                        {venue.name}
                      </div>
                      {venue.venueType && (
                        <div className="flex shrink-0 text-nowrap items-center gap-kit-4 px-kit-8 py-kit-4 rounded-kit-xs bg-[var(--inyc-primary-light)] text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-secondary-medium)] translate-y-[1px]">
                          {venue.venueType.toLowerCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex h-[25px] w-[6.8rem] shrink-0 items-center justify-center gap-kit-4 rounded-kit-full border border-black bg-[var(--inyc-secondary)] px-kit-8 py-kit-2 text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-secondary-text-color)]">
                      <img
                        src={Logo}
                        alt="verified"
                        className="h-[1rem] w-[1.4rem] object-contain invert-[var(--inyc-invert-white)]"
                      />
                      verified
                    </div>
                  </div>
                  <div className="mt-kit-10 flex items-center justify-between gap-kit-12">
                    {venue.address ? (
                      <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-kit-xs font-['Primary-Regular'] text-[var(--inyc-primary-text-color)] opacity-70">
                        {venue.address}
                      </div>
                    ) : (
                      <div />
                    )}
                    <span
                      className={`inline-flex h-[25px] w-[6.8rem] shrink-0 items-center justify-center rounded-kit-full border px-kit-8 py-kit-2 text-center text-kit-2xs font-['Primary-Medium'] ${
                        getVenueResultStatus(venue) === "owned by you"
                          ? "venue-result-owned-pill border-[var(--inyc-outline-color)] bg-white text-black"
                          : getVenueResultStatus(venue) === "claimable"
                            ? "venue-result-claimable-pill border-[var(--inyc-outline-color)] text-black"
                            : "border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)]"
                      }`}
                    >
                      {getVenueResultStatus(venue)}
                    </span>
                  </div>
                </button>
              ))}
              {shouldShowVenueNoResults && (
                <div className="p-kit-16 text-center text-kit-base font-['Primary-Regular'] text-[var(--inyc-primary-text-color)]">
                  no <span className="font-['Primary-Medium']">in—yc</span> venues found for "{venueSearchQuery}"
                </div>
              )}
            </div>
            {shouldShowFallbackActions && (
              <>
                <div className="mx-auto mt-kit-16 flex w-full max-w-[620px] flex-wrap items-center justify-center gap-kit-12">
                  <div className="font-['Primary-Medium'] text-kit-sm text-[var(--inyc-primary-text-color)]">
                    don't see your venue?
                  </div>
                  <div className="flex flex-col items-center gap-kit-20">
                    <Button
                      className="venue-fallback-action-wrap"
                      type="secondary"
                      btnClassName="venue-submit-outline-btn venue-fallback-action-btn !flex !items-center !justify-center !bg-[var(--inyc-primary)] !text-kit-xs !text-[var(--inyc-primary-text-color)] !whitespace-nowrap !border-[var(--inyc-outline-color)] !leading-[1.2]"
                      onClick={handleStartGoogleSearch}
                    >
                      <span className="inline-flex items-center justify-center gap-kit-6 text-kit-xs font-['Primary-Medium'] leading-none">
                        <FcGoogle size={16} className="block" />
                        search google maps
                      </span>
                    </Button>
                    <Button
                      className="venue-fallback-action-wrap"
                      type="secondary"
                      btnClassName="venue-submit-outline-btn venue-fallback-action-btn !flex !items-center !justify-center !bg-[var(--inyc-primary)] !text-kit-xs !text-[var(--inyc-primary-text-color)] !whitespace-nowrap !border-[var(--inyc-outline-color)] !leading-[1.2]"
                      onClick={handleStartManualCreate}
                    >
                      create manually
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {flow === VENUE_FLOW.GOOGLE_SEARCH && (
          <div className="mx-auto max-w-[680px] px-kit-16">
            {renderFlowBack("mb-kit-4")}
            <Input
              value={googleSearchQuery}
              onChange={handleGoogleSearchChange}
              placeHolder="search google maps..."
            />
            <div className="mb-kit-10 mt-kit-20 pl-kit-16 text-kit-base font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
              select your venue:
            </div>
            <div className="flex max-h-[22rem] flex-col gap-kit-12 overflow-y-auto px-kit-2 pb-kit-4 pt-kit-8">
              {googleLoading && (
                <div className="rounded-[var(--inyc-input-border-radius)] border border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16 text-kit-sm font-['Primary-Regular'] text-[var(--inyc-primary-text-color-light)]">
                  searching google maps...
                </div>
              )}
              {!googleLoading && googleSearchQuery.trim().length >= 2 && googleResults.length === 0 && (
                <div className="rounded-[var(--inyc-input-border-radius)] border border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16 text-kit-sm font-['Primary-Regular'] text-[var(--inyc-primary-text-color-light)]">
                  no google results found. try a different search or create manually.
                </div>
              )}
              {!googleLoading && googleResults.map((suggestion, index) => (
                <button
                  key={suggestion.place_id || index}
                  type="button"
                  onClick={() => handleGoogleResultSelect(suggestion)}
                  className="relative w-full overflow-visible rounded-kit-md border border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] px-kit-16 pb-kit-16 pt-kit-20 text-left transition-all duration-200 hover:border-[var(--inyc-secondary)] hover:bg-[var(--inyc-primary)]"
                >
                  <div className="absolute right-[0.75rem] top-[-0.55rem] flex items-center gap-kit-4 rounded-kit-sm border border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-8 py-kit-2 text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                    <FcGoogle size={14} />
                    maps
                  </div>
                  <div className="font-['Primary-Medium'] text-kit-base text-[var(--inyc-primary-text-color)]">
                    {suggestion.structured_formatting?.main_text || suggestion.description}
                  </div>
                  {suggestion.structured_formatting?.secondary_text && (
                    <div className="mt-kit-4 overflow-hidden text-ellipsis whitespace-nowrap text-kit-xs font-['Primary-Regular'] text-[var(--inyc-primary-text-color)] opacity-70">
                      {suggestion.structured_formatting.secondary_text}
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-[calc(1rem+10px)] flex justify-center">
              <Button
                className="w-fit"
                type="secondary"
                btnClassName="venue-submit-outline-btn !flex !items-center !justify-center !w-auto !bg-[var(--inyc-primary)] !text-kit-sm !text-[var(--inyc-primary-text-color)] !whitespace-nowrap !border-[var(--inyc-outline-color)] !py-[var(--inyc-button-compact-py)] !px-kit-12 !leading-[1.2]"
                onClick={handleStartManualCreate}
              >
                create manually instead
              </Button>
            </div>
          </div>
        )}

        {flow === VENUE_FLOW.EXISTING_SELECTED && selectedExistingVenue && (
          <div className="mx-auto max-w-[620px] px-kit-16">
            <div className="mx-auto w-[28rem] max-w-full">
            <VenueCard
              onUnselect={resetVenueFlow}
              venue={selectedVenue.venue}
            />
            {!selectedVenueIsOwned && (
            <div className="mt-kit-16 flex justify-center">
              {selectedVenueIsClaimed ? (
                <Notice className="mb-kit-16">
                  this venue is already claimed
                </Notice>
              ) : selectedVenueCanBeClaimed ? (
                <div>
                  <Notice noicon className="venue-claim-notice mb-kit-16 !border-[var(--inyc-outline-color)]">
                    claim a venue to link it to your account. you'll be able to edit and control what in—yc users see.
                    <br />
                    <br />
                    claim this venue only if you own or represent this business.
                    <br />
                    <br />
                    our team will reach out to verify.
                  </Notice>
                  <Button
                    error={postError}
                    prompt
                    message="confirm?"
                    className="mx-auto w-fit"
                    type="secondary"
                    btnClassName="!flex !items-center !justify-center !w-auto !whitespace-nowrap !py-kit-4 !px-kit-20 !leading-[1.2]"
                    loading={loading}
                    onClick={postVenue}
                    style={{
                      marginBottom: "var(--inyc-space-20)",
                    }}
                  >
                    claim venue
                  </Button>
                </div>
              ) : null}
            </div>
            )}
            </div>
          </div>
        )}
      </div>

      {shouldShowForm && (
        <div className="w-full">
          <div className="relative mx-auto flex min-h-[45px] max-w-[1000px] items-center justify-center px-kit-16">
            {flow !== VENUE_FLOW.EXISTING_SELECTED && (
              <div className="absolute left-[1rem] top-1/2 -translate-y-1/2">
                {renderFlowBack("mb-0")}
              </div>
            )}
            <div
              className="font-['Primary-Medium']"
              style={{
                fontSize: "var(--inyc-text-md)",
                textAlign: "center",
              }}
            >
              {renderFormTitle()}
            </div>
          </div>
          <EditVenue
            key={`edit-venue-${formResetKey}-${flow}`}
            ref={editVenueRef}
            venues={venues}
            refreshVenues={refreshVenues}
            userLocation={userLocation}
            handleVenuPick={handleVenuPick}
            handleInputChange={handleInputChange}
            selectedVenue={selectedVenue}
            setSelectedVenue={setSelectedVenue}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--inyc-space-20)",
              width: "90%",
              margin: "calc(2rem - 35px) auto 1rem auto",
              justifyContent: "center",
            }}
          >
            <Button
              error={postError}
              prompt
              message="confirm?"
              className="mx-auto w-fit"
              type="secondary"
              btnClassName="!flex !items-center !justify-center !w-[147px] !whitespace-nowrap !py-kit-4 !px-kit-20 !leading-[1.2]"
              loading={loading}
              onClick={postVenue}
            >
              {renderSubmitLabel()}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyVenuesPanel;
